/**
 * CR221: Manage Presets Drawer
 *
 * Full preset management in a slide-out drawer.
 * Accessible from the preset picker bar.
 */

import { useMemo, useState } from "react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Search, MoreVertical, Copy, Archive, Trash2, Clock, LayoutGrid, ArrowUpDown, Pencil } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import type { LayoutPreset } from "@/lib/layout-studio/types";

interface ManagePresetsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  presets: LayoutPreset[];
  stagingPresetId: string | null;
  publishPresetId: string | null;
  onSelectPreset: (preset: LayoutPreset) => void;
  onClonePreset: (id: string) => void;
  onArchivePreset: (id: string) => void;
  onDeletePreset: (id: string) => void;

  // ✅ NEW: rename support
  onRenamePreset: (id: string, name: string) => Promise<boolean> | boolean;
}

type SortField = "name" | "updated_at";
type SortDir = "asc" | "desc";

const MAX_NAME_LEN = 80;

export function ManagePresetsDrawer({
  open,
  onOpenChange,
  presets,
  stagingPresetId,
  publishPresetId,
  onSelectPreset,
  onClonePreset,
  onArchivePreset,
  onDeletePreset,
  onRenamePreset,
}: ManagePresetsDrawerProps) {
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState<SortField>("updated_at");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  // ✅ Rename dialog state
  const [renameOpen, setRenameOpen] = useState(false);
  const [renamePresetId, setRenamePresetId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [renaming, setRenaming] = useState(false);

  // Filter and sort presets
  const filteredPresets = useMemo(() => {
    const s = search.toLowerCase();

    return presets
      .filter((p) => {
        const name = (p.name || "").toLowerCase();
        const desc = (p.description || "").toLowerCase();
        return name.includes(s) || desc.includes(s);
      })
      .sort((a, b) => {
        const aVal = sortField === "name" ? a.name : new Date(a.updated_at).getTime();
        const bVal = sortField === "name" ? b.name : new Date(b.updated_at).getTime();
        const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
        return sortDir === "asc" ? cmp : -cmp;
      });
  }, [presets, search, sortField, sortDir]);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const handleSelectAndClose = (preset: LayoutPreset) => {
    onSelectPreset(preset);
    onOpenChange(false);
  };

  const openRename = (preset: LayoutPreset) => {
    setRenamePresetId(preset.id);
    setRenameValue(preset.name ?? "");
    setRenameOpen(true);
  };

  const closeRename = () => {
    setRenameOpen(false);
    setRenamePresetId(null);
    setRenameValue("");
    setRenaming(false);
  };

  const canSaveRename = useMemo(() => {
    const trimmed = renameValue.trim();
    return !!renamePresetId && trimmed.length > 0 && trimmed.length <= MAX_NAME_LEN;
  }, [renamePresetId, renameValue]);

  const submitRename = async () => {
    if (!renamePresetId) return;
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed.length > MAX_NAME_LEN) return;

    setRenaming(true);
    try {
      const ok = await onRenamePreset(renamePresetId, trimmed);
      if (ok) {
        toast.success("Preset renamed", { description: `Renamed to “${trimmed}”.` });
        closeRename();
      } else {
        toast.error("Rename failed", { description: "Could not rename preset." });
      }
    } catch (e) {
      console.error("[ManagePresetsDrawer] rename failed", e);
      toast.error("Rename failed", { description: "Unexpected error while renaming." });
    } finally {
      setRenaming(false);
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-[400px] sm:w-[540px]">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <LayoutGrid className="h-5 w-5" />
              Manage Presets
            </SheetTitle>
            <SheetDescription>View, search, and manage all layout presets</SheetDescription>
          </SheetHeader>

          <div className="mt-4 space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search presets..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Sort Controls */}
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>Sort by:</span>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => toggleSort("name")}>
                Name
                {sortField === "name" && <ArrowUpDown className="h-3 w-3 ml-1" />}
              </Button>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => toggleSort("updated_at")}>
                Updated
                {sortField === "updated_at" && <ArrowUpDown className="h-3 w-3 ml-1" />}
              </Button>
            </div>

            <Separator />

            {/* Preset List */}
            <ScrollArea className="h-[calc(100vh-280px)]">
              <div className="space-y-2 pr-4">
                {filteredPresets.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">No presets found</p>
                ) : (
                  filteredPresets.map((preset) => {
                    const isLive = publishPresetId === preset.id;
                    const isStaging = stagingPresetId === preset.id;

                    return (
                      <div
                        key={preset.id}
                        className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                      >
                        <button className="flex-1 text-left" onClick={() => handleSelectAndClose(preset)}>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">{preset.name}</span>
                            {isLive && (
                              <Badge variant="default" className="text-[9px] px-1.5 py-0">
                                LIVE
                              </Badge>
                            )}
                            {isStaging && (
                              <Badge variant="secondary" className="text-[9px] px-1.5 py-0">
                                STAGING
                              </Badge>
                            )}
                            {!isLive && !isStaging && preset.status === "draft" && (
                              <Badge variant="outline" className="text-[9px] px-1.5 py-0">
                                Draft
                              </Badge>
                            )}
                          </div>
                          {preset.description && (
                            <p className="text-xs text-muted-foreground truncate mt-0.5">{preset.description}</p>
                          )}
                          <p className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDistanceToNow(new Date(preset.updated_at), {
                              addSuffix: true,
                            })}
                          </p>
                        </button>

                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-popover border shadow-lg z-50">
                            <DropdownMenuItem onClick={() => onClonePreset(preset.id)}>
                              <Copy className="h-4 w-4 mr-2" />
                              Clone
                            </DropdownMenuItem>

                            {/* ✅ Rename */}
                            <DropdownMenuItem onClick={() => openRename(preset)}>
                              <Pencil className="h-4 w-4 mr-2" />
                              Rename
                            </DropdownMenuItem>

                            <DropdownMenuItem onClick={() => onArchivePreset(preset.id)} disabled={isLive || isStaging}>
                              <Archive className="h-4 w-4 mr-2" />
                              Archive
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => onDeletePreset(preset.id)}
                              disabled={isLive || isStaging}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    );
                  })
                )}
              </div>
            </ScrollArea>

            {/* Summary */}
            <div className="pt-2 border-t text-xs text-muted-foreground">
              {presets.length} preset{presets.length !== 1 ? "s" : ""} total
              {publishPresetId && " • 1 live"}
              {stagingPresetId && " • 1 staging"}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* ✅ Rename Dialog */}
      <Dialog open={renameOpen} onOpenChange={(o) => (o ? setRenameOpen(true) : closeRename())}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename preset</DialogTitle>
            <DialogDescription>Update the display name for this preset.</DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-2">
            <Label>Name</Label>
            <Input
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              maxLength={MAX_NAME_LEN}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  submitRename();
                }
              }}
            />
            <div className="text-xs text-muted-foreground">
              {renameValue.trim().length}/{MAX_NAME_LEN}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeRename} disabled={renaming}>
              Cancel
            </Button>
            <Button onClick={submitRename} disabled={!canSaveRename || renaming}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
